import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';

export interface DonorMismatchData {
  existingDonorId?: string | null;
  existingRegistrationNumber?: string | null;
  existingPedigreeName?: string | null;
  existingBirthDate?: string | null;
  inputRegistrationNumber?: string | null;
  inputPedigreeName?: string | null;
  inputBirthDate?: string | null;
}

interface DonorMismatchDialogProps {
  visible: boolean;
  mismatchData: DonorMismatchData | null;
  donorName: string;
  onCreateNew: () => void;
  onUseExisting: () => void;
  onUpdateExisting?: () => void;
  onCancel: () => void;
}

interface ComparisonRow {
  field: string;
  existing: string;
  input: string;
}

export function DonorMismatchDialog({
  visible,
  mismatchData,
  donorName,
  onCreateNew,
  onUseExisting,
  onUpdateExisting,
  onCancel,
}: DonorMismatchDialogProps) {
  if (!mismatchData) {
    return null;
  }

  const comparisonData: ComparisonRow[] = [];

  if (
    mismatchData.existingRegistrationNumber !==
    mismatchData.inputRegistrationNumber
  ) {
    comparisonData.push({
      field: 'Registration Number',
      existing: mismatchData.existingRegistrationNumber || 'N/A',
      input: mismatchData.inputRegistrationNumber || 'N/A',
    });
  }

  if (mismatchData.existingPedigreeName !== mismatchData.inputPedigreeName) {
    comparisonData.push({
      field: 'Pedigree Name',
      existing: mismatchData.existingPedigreeName || 'N/A',
      input: mismatchData.inputPedigreeName || 'N/A',
    });
  }

  if (mismatchData.existingBirthDate !== mismatchData.inputBirthDate) {
    comparisonData.push({
      field: 'Birth Date',
      existing: mismatchData.existingBirthDate || 'N/A',
      input: mismatchData.inputBirthDate || 'N/A',
    });
  }

  const footer = (
    <div className="flex justify-end gap-2">
      <Button label="Cancel" severity="secondary" onClick={onCancel} outlined />
      <Button
        label="Create New Donor"
        severity="warning"
        onClick={onCreateNew}
      />
      {onUpdateExisting && (
        <Button
          label="Update Existing Donor"
          severity="info"
          onClick={onUpdateExisting}
        />
      )}
      <Button
        label="Use Existing Data"
        severity="success"
        onClick={onUseExisting}
      />
    </div>
  );

  return (
    <Dialog
      header="Donor Data Mismatch Detected"
      visible={visible}
      style={{ width: '50vw' }}
      onHide={onCancel}
      footer={footer}
      modal
      dismissableMask={false}
    >
      <div className="mb-4">
        <p className="mb-2">
          A donor with the ID <strong>"{donorName}"</strong> already exists but
          has different information:
        </p>
      </div>

      <DataTable value={comparisonData} className="mb-4">
        <Column field="field" header="Field" />
        <Column field="existing" header="Existing in Database" />
        <Column field="input" header="Your Input" />
      </DataTable>

      <div className="text-sm text-gray-600">
        <p className="mb-2">
          <strong>Create New Donor:</strong> Keep your uploaded data as-is and
          create a new donor record (intentional duplicate).
        </p>
        {onUpdateExisting && (
          <p className="mb-2">
            <strong>Update Existing Donor:</strong> Update the existing donor
            record with your uploaded data (registration, name, birth date).
          </p>
        )}
        <p>
          <strong>Use Existing Data:</strong> Replace your uploaded values with
          the existing donor data from the database.
        </p>
      </div>
    </Dialog>
  );
}
